import React from 'react';
import { Checkbox, Input } from '@fluentui/react-components';
import { GuideTable, GuideTh, guideCellClassName as cell, guideRowClassName } from '../../../../theme/components/GuideTable';
import { GuideEmptyState } from '../../../../theme/components/GuideEmptyState';
import { StatusPill } from '../../../../theme/components/StatusPill';
import {
    appliedTotalsByCurrency,
    exceedsPending,
    InvoiceSelection,
    invoiceLabel,
    isSuggestedInvoice,
    pendingAmountOf,
    selectionFor,
    validateSelections
} from '../../../../functions/CreditorInvoices.Functions';
import { CreditorInvoice } from '../../../../models/Entities/CashFlow/CreditorInvoice/CreditorInvoice';
import { fmtInvoiceAmount, fmtInvoiceDate, InvoiceStatusPill, useInvoicesText } from './PaymentInvoices.Bits';
import './PaymentInvoices.Styles.css';

export interface InvoicePickerProps {
    /** Facturas a mostrar, ya en el orden sugerido. */
    invoices: CreditorInvoice[];
    value: InvoiceSelection[];
    onChange: (value: InvoiceSelection[]) => void;
    /** Liquidaciones que se están pagando: sus facturas se marcan como sugeridas. */
    suggestedSettlementIds?: (number | null | undefined)[];
    settlementNameOf: (id: number) => string;
    /** Lo que este mismo pago ya tenía aplicado por factura (edición): no cuenta como exceso. */
    alreadyApplied?: Record<number, number>;
    disabled?: boolean;
    testId?: string;
}

/**
 * Selección múltiple de facturas con importe aplicado opcional (precargado con lo pendiente de la
 * factura, en su moneda). Grilla de la guía: casilla, factura, liquidación, importe, pendiente, aplicado.
 */
export const InvoicePicker = ({ invoices, value, onChange, suggestedSettlementIds = [], settlementNameOf, alreadyApplied = {}, disabled, testId = 'invoice-picker' }: InvoicePickerProps) => {
    const tr = useInvoicesText();
    const errors = validateSelections(value);

    if (invoices.length === 0) {
        return <GuideEmptyState title={tr('picker.emptyTitle')} text={tr('picker.emptyText')} data-testid={`${testId}-empty`} />;
    }

    const selectedOf = (id: number) => value.find((item) => item.invoiceId === id);
    const toggle = (invoice: CreditorInvoice) => {
        const current = selectedOf(invoice.id);
        onChange(current ? value.filter((item) => item.invoiceId !== invoice.id) : [...value, selectionFor(invoice)]);
    };
    const setAmount = (invoiceId: number, amountText: string) =>
        onChange(value.map((item) => (item.invoiceId === invoiceId ? { ...item, amountText } : item)));

    const totals = appliedTotalsByCurrency(value, invoices);

    return (
        <div className="pinv-picker" data-testid={testId}>
            <GuideTable ariaLabel={tr('picker.title')}>
                <thead>
                    <tr>
                        <th className={cell('check')} scope="col"><span className="pinv-sr">{tr('picker.select', { name: '' })}</span></th>
                        <GuideTh>{tr('columns.number')}</GuideTh>
                        <GuideTh>{tr('columns.settlement')}</GuideTh>
                        <GuideTh align="end">{tr('columns.amount')}</GuideTh>
                        <GuideTh>{tr('columns.status')}</GuideTh>
                        <GuideTh align="end">{tr('picker.applied')}</GuideTh>
                    </tr>
                </thead>
                <tbody>
                    {invoices.map((invoice) => {
                        const selection = selectedOf(invoice.id);
                        const name = invoiceLabel(invoice);
                        const pending = pendingAmountOf(invoice);
                        const suggested = isSuggestedInvoice(invoice, suggestedSettlementIds);
                        const error = errors[invoice.id];
                        const exceeds = !!selection && !error && exceedsPending(invoice, selection.amountText, alreadyApplied[invoice.id] ?? 0);
                        return (
                            <tr key={invoice.id} className={guideRowClassName({ selected: !!selection })} data-testid={`${testId}-row-${invoice.id}`}>
                                <td className={cell('check')}>
                                    <Checkbox
                                        checked={!!selection}
                                        disabled={disabled}
                                        aria-label={tr('picker.select', { name })}
                                        onChange={() => toggle(invoice)}
                                    />
                                </td>
                                <td>
                                    <span className="pinv-strong">{name}</span>
                                    <span className="pinv-secondary ichtys-num">{fmtInvoiceDate(invoice.invoiceDate)}</span>
                                    {suggested && <StatusPill tone="brand" symbol="■" className="pinv-pill">{tr('picker.suggested')}</StatusPill>}
                                </td>
                                <td className="pinv-secondary-cell">{invoice.settlementId ? settlementNameOf(invoice.settlementId) : '—'}</td>
                                <td className={cell('end', 'num')}>
                                    {fmtInvoiceAmount(invoice.amount, invoice.currencyCode)}
                                    {pending !== undefined && (
                                        <span className="pinv-secondary">{tr('picker.pending', { amount: fmtInvoiceAmount(pending, invoice.currencyCode) })}</span>
                                    )}
                                </td>
                                <td><InvoiceStatusPill invoice={invoice} /></td>
                                <td className={cell('end')}>
                                    {selection ? (
                                        <div className="pinv-applied">
                                            <Input
                                                className="pinv-applied__input"
                                                inputMode="decimal"
                                                placeholder="0,00"
                                                value={selection.amountText}
                                                disabled={disabled}
                                                aria-label={tr('picker.appliedAria', { name })}
                                                aria-invalid={!!error || undefined}
                                                contentBefore={<span className="pinv-currency">{(invoice.currencyCode || '—').toUpperCase()}</span>}
                                                onChange={(_event, data) => setAmount(invoice.id, data.value)}
                                            />
                                            {error && <span className="pinv-error" role="alert">{tr('picker.appliedInvalid')}</span>}
                                            {exceeds && <span className="pinv-warning" data-testid={`${testId}-exceeds-${invoice.id}`}>{tr('picker.exceeds')}</span>}
                                        </div>
                                    ) : <span className="pinv-secondary-cell">—</span>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </GuideTable>
            {value.length > 0 && (
                <p className="ichtys-secondary pinv-summary" data-testid={`${testId}-summary`}>
                    {tr('picker.selected', { count: value.length })}
                    {totals.length > 0 && <> · {tr('picker.total', { amounts: totals.map((item) => fmtInvoiceAmount(item.amount, item.currency)).join(' · ') })}</>}
                </p>
            )}
        </div>
    );
};

export default InvoicePicker;
